/**
 * LicenciasModule.jsx — Gestión de Licencias de Conducir.
 * Visible para: control_taller, admin.
 *
 * Vista 1: Lista de choferes con badge de estatus de licencia.
 * Vista 2: Detalle de un chofer (editar datos + agregar fotos de unidad + historial).
 */
import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Helpers ────────────────────────────────────────────────────────────────

const ESTATUS_LIC = {
  VIGENTE:     { lbl: "Vigente",      bg: "#DCFCE7", color: "#15803D", icon: "fa-circle-check" },
  POR_VENCER:  { lbl: "Por vencer",   bg: "#FEF3C7", color: "#B45309", icon: "fa-triangle-exclamation" },
  VENCIDA:     { lbl: "Vencida",      bg: "#FEE2E2", color: "#991B1B", icon: "fa-circle-xmark" },
  SIN_CAPTURAR:{ lbl: "Sin capturar", bg: "#F1F5F9", color: "#64748B", icon: "fa-circle-question" },
};

function badgeLicencia(estatus) {
  const e = ESTATUS_LIC[estatus] || ESTATUS_LIC.SIN_CAPTURAR;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full"
          style={{ background: e.bg, color: e.color }}>
      <i className={`fa-solid ${e.icon}`}/> {e.lbl}
    </span>
  );
}

function iniciales(nombre = "") {
  return nombre.split(" ").filter(Boolean).slice(0, 2).map(p => p[0]).join("").toUpperCase();
}

function fmtFecha(iso) {
  if (!iso) return "—";
  return iso.slice(0, 10);
}

function diasRestantes(fv) {
  if (!fv) return null;
  const hoy = new Date(); hoy.setHours(0,0,0,0);
  const vig = new Date(fv + "T00:00:00");
  return Math.round((vig - hoy) / 86400000);
}

function fileToDataURL(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = (e) => res(e.target.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

// ── Componente principal ───────────────────────────────────────────────────

export default function LicenciasModule() {
  const { user } = useAuth();
  const [lista, setLista]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [busqueda, setBusqueda]   = useState("");
  const [detalle, setDetalle]     = useState(null);   // objeto chofer o null
  const [loadingDet, setLoadingDet] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/licencias`);
      setLista(r.data || []);
    } catch { toast.error("No se pudo cargar la lista de licencias"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirDetalle = async (chofer) => {
    setLoadingDet(true);
    try {
      const r = await axios.get(`${API}/licencias/${chofer.id}`);
      setDetalle(r.data);
    } catch { toast.error("No se pudo cargar el detalle"); }
    finally { setLoadingDet(false); }
  };

  const cerrarDetalle = () => setDetalle(null);

  // ── Resumen ──────────────────────────────────────────────────────────────
  const conteo = { VIGENTE: 0, POR_VENCER: 0, VENCIDA: 0, SIN_CAPTURAR: 0 };
  lista.forEach(c => { conteo[c.licencia_estatus] = (conteo[c.licencia_estatus] || 0) + 1; });

  const filtrada = lista.filter(c =>
    !busqueda || c.nombre?.toLowerCase().includes(busqueda.toLowerCase())
  );

  // ── Render detalle ───────────────────────────────────────────────────────
  if (detalle) {
    return (
      <DetalleChofer
        chofer={detalle}
        onClose={cerrarDetalle}
        onSaved={() => { cargar(); abrirDetalle(detalle); }}
        user={user}
      />
    );
  }

  // ── Render lista ─────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50 pb-12">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <i className="fa-solid fa-id-card text-violet-600"/>
              Licencias de Conducir
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">Control y vencimiento de licencias de choferes</p>
          </div>
          <button onClick={cargar}
                  className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5">
            <i className="fa-solid fa-rotate"/> Actualizar
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Tarjetas de resumen */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { key: "VIGENTE",      lbl: "Vigentes",      icon: "fa-circle-check",         color: "#15803D", bg: "#DCFCE7" },
            { key: "POR_VENCER",   lbl: "Por vencer",    icon: "fa-triangle-exclamation",  color: "#B45309", bg: "#FEF3C7" },
            { key: "VENCIDA",      lbl: "Vencidas",      icon: "fa-circle-xmark",          color: "#991B1B", bg: "#FEE2E2" },
            { key: "SIN_CAPTURAR", lbl: "Sin capturar",  icon: "fa-circle-question",       color: "#64748B", bg: "#F1F5F9" },
          ].map(({ key, lbl, icon, color, bg }) => (
            <div key={key} className="rounded-xl p-4 border flex items-center gap-3"
                 style={{ background: bg, borderColor: color + "40" }}>
              <i className={`fa-solid ${icon} text-2xl`} style={{ color }}/>
              <div>
                <div className="text-2xl font-bold" style={{ color }}>{conteo[key] || 0}</div>
                <div className="text-xs font-medium" style={{ color }}>{lbl}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Buscador */}
        <div className="relative max-w-xs">
          <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"/>
          <input
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-violet-400"
            placeholder="Buscar chofer…"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
          />
        </div>

        {/* Lista */}
        {loading ? (
          <div className="text-center text-slate-400 py-12"><i className="fa-solid fa-spinner fa-spin fa-2x"/></div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            {filtrada.length === 0 ? (
              <div className="text-center text-slate-400 py-12 text-sm">Sin choferes que mostrar</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50 text-left text-xs text-slate-500 uppercase tracking-wide">
                    <th className="px-4 py-3">Chofer</th>
                    <th className="px-4 py-3">Licencia</th>
                    <th className="px-4 py-3">Vigencia</th>
                    <th className="px-4 py-3">Estatus</th>
                    <th className="px-4 py-3">Fotos</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtrada.map(c => {
                    const dias = diasRestantes(c.licencia_fecha_vigencia);
                    return (
                      <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-violet-100 flex items-center justify-center text-xs font-bold text-violet-700 flex-shrink-0">
                              {c.foto_perfil
                                ? <img src={c.foto_perfil} alt="" className="w-9 h-9 rounded-full object-cover"/>
                                : iniciales(c.nombre)}
                            </div>
                            <div>
                              <div className="font-medium text-slate-800">{c.nombre}</div>
                              <div className="text-xs text-slate-400">{c.telefono || "—"}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-mono text-xs text-slate-700">{c.licencia_numero || <span className="text-slate-300">—</span>}</div>
                          {c.licencia_tipo && <div className="text-xs text-slate-500">{c.licencia_tipo}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-700">{fmtFecha(c.licencia_fecha_vigencia)}</div>
                          {dias !== null && (
                            <div className={`text-xs ${dias < 0 ? "text-red-600" : dias <= 30 ? "text-amber-600" : "text-slate-400"}`}>
                              {dias < 0 ? `Vencida hace ${Math.abs(dias)} día(s)` : `${dias} día(s) restantes`}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">{badgeLicencia(c.licencia_estatus)}</td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-slate-500">
                            <i className="fa-solid fa-camera mr-1"/>{c.fotos_count || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => abrirDetalle(c)}
                            disabled={loadingDet}
                            className="text-xs px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 transition-colors"
                          >
                            Ver detalle
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── DetalleChofer ──────────────────────────────────────────────────────────

function DetalleChofer({ chofer, onClose, onSaved, user }) {
  const [editando, setEditando]     = useState(false);
  const [form, setForm]             = useState({
    licencia_numero:           chofer.licencia_numero    || "",
    licencia_tipo:             chofer.licencia_tipo      || "",
    licencia_fecha_expedicion: chofer.licencia_fecha_expedicion || "",
    licencia_fecha_vigencia:   chofer.licencia_fecha_vigencia   || "",
  });
  const [guardando, setGuardando]   = useState(false);
  const [modalFoto, setModalFoto]   = useState(false);

  const handleGuardar = async () => {
    setGuardando(true);
    try {
      await axios.put(`${API}/licencias/${chofer.id}`, form);
      toast.success("Licencia actualizada");
      setEditando(false);
      onSaved();
    } catch (e) {
      toast.error("Error al guardar: " + (e.response?.data?.detail || e.message));
    } finally { setGuardando(false); }
  };

  const estatus = calcEstatus(chofer.licencia_fecha_vigencia);
  const dias    = diasRestantes(chofer.licencia_fecha_vigencia);

  return (
    <div className="min-h-screen bg-slate-50 pb-12">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button onClick={onClose}
                  className="text-slate-500 hover:text-slate-800 flex items-center gap-1.5 text-sm">
            <i className="fa-solid fa-arrow-left"/> Volver a la lista
          </button>
          <div className="h-5 w-px bg-slate-200"/>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center text-sm font-bold text-violet-700">
              {chofer.foto_perfil
                ? <img src={chofer.foto_perfil} alt="" className="w-10 h-10 rounded-full object-cover"/>
                : iniciales(chofer.nombre)}
            </div>
            <div>
              <h1 className="text-base font-bold text-slate-900">{chofer.nombre}</h1>
              <div className="text-xs text-slate-500">{chofer.telefono}</div>
            </div>
          </div>
          <div className="ml-auto">{badgeLicencia(estatus)}</div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">

        {/* Datos de licencia */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2">
              <i className="fa-solid fa-id-card text-violet-500"/> Datos de licencia
            </h2>
            <div className="flex items-center gap-2">
              <a href={`${API}/licencias/${chofer.id}/exportar`}
                 download
                 className="text-xs px-3 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 flex items-center gap-1.5 transition-colors"
                 title="Descargar Excel con datos de licencia, fotos e historial">
                <i className="fa-solid fa-file-excel"/> Exportar Excel
              </a>
              {!editando && (
                <button onClick={() => setEditando(true)}
                        className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5">
                  <i className="fa-solid fa-pen"/> Editar
                </button>
              )}
            </div>
          </div>
          <div className="p-5">
            {editando ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Número de licencia</span>
                    <input className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                           value={form.licencia_numero}
                           onChange={e => setForm(f => ({...f, licencia_numero: e.target.value}))}
                           placeholder="Ej. H123456"/>
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Tipo</span>
                    <input className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                           value={form.licencia_tipo}
                           onChange={e => setForm(f => ({...f, licencia_tipo: e.target.value}))}
                           placeholder="Ej. chofer, federal, B, C"/>
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Fecha de expedición</span>
                    <input type="date" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                           value={form.licencia_fecha_expedicion}
                           onChange={e => setForm(f => ({...f, licencia_fecha_expedicion: e.target.value}))}/>
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Fecha de vigencia (vencimiento)</span>
                    <input type="date" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                           value={form.licencia_fecha_vigencia}
                           onChange={e => setForm(f => ({...f, licencia_fecha_vigencia: e.target.value}))}/>
                  </label>
                </div>
                <div className="flex gap-2 pt-2">
                  <button onClick={handleGuardar} disabled={guardando}
                          className="text-sm px-4 py-2 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 flex items-center gap-2">
                    {guardando && <i className="fa-solid fa-spinner fa-spin"/>}
                    Guardar
                  </button>
                  <button onClick={() => setEditando(false)}
                          className="text-sm px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50">
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <DatoLic label="Número" value={chofer.licencia_numero}/>
                <DatoLic label="Tipo"   value={chofer.licencia_tipo}/>
                <DatoLic label="Expedición" value={fmtFecha(chofer.licencia_fecha_expedicion)}/>
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Vigencia</div>
                  <div className="font-mono text-sm font-semibold text-slate-800">{fmtFecha(chofer.licencia_fecha_vigencia)}</div>
                  {dias !== null && (
                    <div className={`text-xs mt-0.5 ${dias < 0 ? "text-red-600 font-medium" : dias <= 30 ? "text-amber-600 font-medium" : "text-slate-400"}`}>
                      {dias < 0 ? `Vencida hace ${Math.abs(dias)} día(s)` : `${dias} día(s)`}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          {chofer.licencia_actualizada_por && (
            <div className="px-5 py-2 border-t border-slate-100 text-xs text-slate-400">
              Última actualización: <strong>{chofer.licencia_actualizada_por}</strong>
              {chofer.licencia_actualizada_en && <> · {fmtFecha(chofer.licencia_actualizada_en)}</>}
            </div>
          )}
        </div>

        {/* Fotos de unidades confirmadas */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2">
              <i className="fa-solid fa-camera text-violet-500"/> Unidades confirmadas con foto
            </h2>
            <button onClick={() => setModalFoto(true)}
                    className="text-xs px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 flex items-center gap-1.5">
              <i className="fa-solid fa-plus"/> Agregar unidad
            </button>
          </div>
          <div className="p-5">
            {(!chofer.licencia_fotos_historial || chofer.licencia_fotos_historial.length === 0) ? (
              <div className="text-sm text-slate-400 text-center py-6">Sin fotos de unidades confirmadas</div>
            ) : (
              <div className="space-y-3">
                {[...chofer.licencia_fotos_historial].reverse().map((entrada, i) => (
                  <div key={i} className="flex items-start gap-4 p-3 rounded-lg border border-slate-100 bg-slate-50">
                    <div className="flex gap-2">
                      <FotoMiniatura url={entrada.foto_frente} label="Frente"/>
                      <FotoMiniatura url={entrada.foto_reverso} label="Reverso"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-slate-800">Unidad {entrada.unidad}</div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        <i className="fa-solid fa-calendar mr-1"/>{fmtFecha(entrada.confirmada_en)}
                        {" · "}<i className="fa-solid fa-user mr-1"/>{entrada.capturado_por}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Historial de unidades (eventos_chofer) */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2">
              <i className="fa-solid fa-truck-droplet text-violet-500"/> Historial de unidades manejadas
            </h2>
            <p className="text-xs text-slate-400 mt-1">
              De registros de actividad vía bot WhatsApp.
              Las unidades sin 📷 no tienen foto de licencia de respaldo.
            </p>
          </div>
          <div className="overflow-x-auto">
            {(!chofer.historial_unidades || chofer.historial_unidades.length === 0) ? (
              <div className="text-sm text-slate-400 text-center py-8">Sin historial de unidades</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50 text-left text-xs text-slate-500 uppercase tracking-wide">
                    <th className="px-4 py-3">Unidad</th>
                    <th className="px-4 py-3">Primera vez</th>
                    <th className="px-4 py-3">Última vez</th>
                    <th className="px-4 py-3">Días de uso</th>
                    <th className="px-4 py-3">Foto licencia</th>
                  </tr>
                </thead>
                <tbody>
                  {chofer.historial_unidades.map((h, i) => (
                    <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono font-semibold text-slate-800">{h.unidad}</td>
                      <td className="px-4 py-3 text-slate-600">{fmtFecha(h.primera_vez)}</td>
                      <td className="px-4 py-3 text-slate-600">{fmtFecha(h.ultima_vez)}</td>
                      <td className="px-4 py-3 text-slate-600">{h.dias_uso}</td>
                      <td className="px-4 py-3">
                        {h.tiene_foto_licencia
                          ? <span className="text-green-600 font-medium"><i className="fa-solid fa-camera mr-1"/>Con foto</span>
                          : <span className="text-slate-400 text-xs">Sin respaldo</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Modal agregar foto */}
      {modalFoto && (
        <ModalAgregarFoto
          chofer={chofer}
          onClose={() => setModalFoto(false)}
          onSaved={() => { setModalFoto(false); onSaved(); }}
        />
      )}
    </div>
  );
}

// ── Sub-componentes ────────────────────────────────────────────────────────

function DatoLic({ label, value }) {
  return (
    <div>
      <div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">{label}</div>
      <div className="font-mono text-sm font-semibold text-slate-800">{value || <span className="text-slate-300 font-normal">—</span>}</div>
    </div>
  );
}

function FotoMiniatura({ url, label }) {
  if (!url) return null;
  const esDataURL = url.startsWith("data:");
  return (
    <div className="flex flex-col items-center gap-1">
      <a href={url} target="_blank" rel="noreferrer">
        <img src={url} alt={label}
             className="w-16 h-16 object-cover rounded-lg border border-slate-200 hover:opacity-80 transition-opacity cursor-pointer"/>
      </a>
      <span className="text-[10px] text-slate-400">{label}</span>
      {!esDataURL && (
        <a href={url} download className="text-[10px] text-violet-600 hover:underline">
          <i className="fa-solid fa-download"/>
        </a>
      )}
    </div>
  );
}

function calcEstatus(fv) {
  if (!fv) return "SIN_CAPTURAR";
  const hoy = new Date(); hoy.setHours(0,0,0,0);
  const vig = new Date(fv + "T00:00:00");
  const dias = Math.round((vig - hoy) / 86400000);
  if (dias < 0) return "VENCIDA";
  if (dias <= 30) return "POR_VENCER";
  return "VIGENTE";
}

function ModalAgregarFoto({ chofer, onClose, onSaved }) {
  const [unidad, setUnidad]         = useState("");
  const [fotoF,  setFotoF]          = useState(null);   // dataURL
  const [fotoR,  setFotoR]          = useState(null);
  const [prevF,  setPrevF]          = useState(null);
  const [prevR,  setPrevR]          = useState(null);
  const [enviando, setEnviando]     = useState(false);
  const refF = useRef(); const refR = useRef();

  const onFileF = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const d = await fileToDataURL(f);
    setFotoF(d); setPrevF(d);
  };
  const onFileR = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const d = await fileToDataURL(f);
    setFotoR(d); setPrevR(d);
  };

  const handleEnviar = async () => {
    if (!unidad.trim()) { toast.error("Escribe el número de unidad"); return; }
    if (!fotoF)         { toast.error("Agrega la foto de frente"); return; }
    if (!fotoR)         { toast.error("Agrega la foto de reverso"); return; }
    setEnviando(true);
    try {
      await axios.post(`${API}/licencias/${chofer.id}/foto`, {
        unidad: unidad.trim(),
        foto_frente:  fotoF,
        foto_reverso: fotoR,
      });
      toast.success("Fotos guardadas");
      onSaved();
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
    } finally { setEnviando(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
         onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-5"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-slate-900 flex items-center gap-2">
            <i className="fa-solid fa-camera text-violet-500"/> Agregar unidad confirmada
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <i className="fa-solid fa-xmark"/>
          </button>
        </div>

        <div>
          <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">
            Número de unidad
          </label>
          <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                 value={unidad} onChange={e => setUnidad(e.target.value)}
                 placeholder="Ej. 69422"/>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {[
            { label: "Foto frente", ref: refF, prev: prevF, onChange: onFileF },
            { label: "Foto reverso", ref: refR, prev: prevR, onChange: onFileR },
          ].map(({ label, ref, prev, onChange }) => (
            <div key={label}>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">{label}</label>
              <div className="border-2 border-dashed border-slate-200 rounded-xl flex flex-col items-center justify-center aspect-square overflow-hidden cursor-pointer hover:border-violet-400 transition-colors"
                   onClick={() => ref.current?.click()}>
                {prev
                  ? <img src={prev} alt={label} className="w-full h-full object-cover"/>
                  : <div className="text-center text-slate-400 text-xs p-3">
                      <i className="fa-solid fa-camera fa-2x mb-1"/>
                      <div>Toca para agregar</div>
                    </div>
                }
              </div>
              <input ref={ref} type="file" accept="image/*" capture="environment"
                     className="hidden" onChange={onChange}/>
            </div>
          ))}
        </div>

        <div className="flex gap-3">
          <button onClick={handleEnviar} disabled={enviando}
                  className="flex-1 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 flex items-center justify-center gap-2">
            {enviando && <i className="fa-solid fa-spinner fa-spin"/>}
            Guardar fotos
          </button>
          <button onClick={onClose}
                  className="px-4 py-2.5 rounded-xl border border-slate-200 text-sm hover:bg-slate-50">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
